import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/shared/lib/redux/hooks';
import {
  cartCleared,
  lineAdded,
  lineRefreshed,
  lineRemoved,
  linesAdded,
  qtySet,
} from './cartSlice';
import type { AddLinePayload } from './cartSlice';
import { cartLinesCount, cartTotalOf, isCartFull, toLineViews } from './cart.logic';
import type { CartLineSnapshot, CartLineView } from './types';

function useCurrentStoreId(): string | null {
  return useAppSelector((state) => state.store.currentStoreId);
}

export interface StoreCartView {
  storeId: string | null;
  lines: CartLineView[];
  /** Distinct products in the cart (drives the header badge). */
  linesCount: number;
  /** Decimal string (14,2). */
  total: string;
  isFull: boolean;
}

/** The current store's cart, with computed line totals and the cart total. */
export function useStoreCart(): StoreCartView {
  const storeId = useCurrentStoreId();
  const cart = useAppSelector((state) => (storeId ? state.cart[storeId] : undefined));
  return useMemo(
    () => ({
      storeId,
      lines: toLineViews(cart),
      linesCount: cartLinesCount(cart),
      total: cartTotalOf(cart),
      isFull: isCartFull(cart),
    }),
    [storeId, cart],
  );
}

/** Distinct products in the current store's cart — cheap selector for badges. */
export function useCartLinesCount(): number {
  const storeId = useCurrentStoreId();
  return useAppSelector((state) => (storeId ? cartLinesCount(state.cart[storeId]) : 0));
}

/** Quantity of one product in the current store's cart (`undefined` when it isn't there). */
export function useCartQty(productId: string): string | undefined {
  const storeId = useCurrentStoreId();
  return useAppSelector((state) => (storeId ? state.cart[storeId]?.[productId]?.qty : undefined));
}

export function useIsCartFull(): boolean {
  const storeId = useCurrentStoreId();
  return useAppSelector((state) => (storeId ? isCartFull(state.cart[storeId]) : false));
}

export interface CartActions {
  add: (productId: string, snapshot: CartLineSnapshot, qty?: string) => void;
  addMany: (lines: Array<Omit<AddLinePayload, 'storeId'>>) => void;
  setQty: (productId: string, qty: string) => void;
  remove: (productId: string) => void;
  /** Replaces a line's snapshot and quantity with fresh catalog data. */
  refresh: (productId: string, snapshot: CartLineSnapshot, qty: string) => void;
  clear: () => void;
}

/** Cart mutations bound to the current store; every one is a no-op while no store is selected. */
export function useCartActions(): CartActions {
  const dispatch = useAppDispatch();
  const storeId = useCurrentStoreId();

  const add = useCallback<CartActions['add']>(
    (productId, snapshot, qty) => {
      if (storeId) dispatch(lineAdded({ storeId, productId, snapshot, qty }));
    },
    [dispatch, storeId],
  );
  const addMany = useCallback<CartActions['addMany']>(
    (lines) => {
      if (storeId) dispatch(linesAdded({ storeId, lines: lines.map((l) => ({ ...l, storeId })) }));
    },
    [dispatch, storeId],
  );
  const setQty = useCallback<CartActions['setQty']>(
    (productId, qty) => {
      if (storeId) dispatch(qtySet({ storeId, productId, qty }));
    },
    [dispatch, storeId],
  );
  const remove = useCallback<CartActions['remove']>(
    (productId) => {
      if (storeId) dispatch(lineRemoved({ storeId, productId }));
    },
    [dispatch, storeId],
  );
  const refresh = useCallback<CartActions['refresh']>(
    (productId, snapshot, qty) => {
      if (storeId) dispatch(lineRefreshed({ storeId, productId, snapshot, qty }));
    },
    [dispatch, storeId],
  );
  const clear = useCallback(() => {
    if (storeId) dispatch(cartCleared({ storeId }));
  }, [dispatch, storeId]);

  return useMemo(
    () => ({ add, addMany, setQty, remove, refresh, clear }),
    [add, addMany, setQty, remove, refresh, clear],
  );
}
